package com.fantasytenis.web.components

import androidx.compose.runtime.*
import com.fantasytenis.web.theme.Theme
import com.fantasytenis.web.utils.WebPreferences
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Onboarding screen data for the first-time user modal.
 */
private data class OnboardingScreen(
    val emoji: String,
    val title: String,
    val subtitle: String,
    val highlights: List<OnboardingHighlight>
)

private data class OnboardingHighlight(
    val emoji: String,
    val category: String,
    val description: String,
    val color: String
)

/**
 * FirstTimeUserModal - 3-screen onboarding walkthrough for new users.
 *
 * Explains the unique 5-category Perfect Player scoring system
 * and introduces users to the Baseline fantasy tennis experience.
 *
 * @param onComplete Callback when user finishes or skips onboarding
 */
@Composable
fun FirstTimeUserModal(
    onComplete: () -> Unit
) {
    val colors = Theme.colors
    var currentScreen by remember { mutableStateOf(0) }

    val screens = listOf(
        OnboardingScreen(
            emoji = "\uD83C\uDFBE", // 🎾
            title = "Welcome to Baseline",
            subtitle = "Fantasy tennis built for real fans",
            highlights = listOf(
                OnboardingHighlight(
                    emoji = "\uD83C\uDFC6", // 🏆
                    category = "Daily Contests",
                    description = "Compete in contests during every ATP & WTA tournament",
                    color = "#FFD700"
                ),
                OnboardingHighlight(
                    emoji = "\u26A1", // ⚡
                    category = "Live Scoring",
                    description = "Watch your points update in real-time as matches unfold",
                    color = "#2196F3"
                ),
                OnboardingHighlight(
                    emoji = "\uD83D\uDCB0", // 💰
                    category = "Win Prizes",
                    description = "Cash prizes for top performers in every contest",
                    color = "#4CAF50"
                )
            )
        ),
        OnboardingScreen(
            emoji = "\uD83C\uDFAF", // 🎯
            title = "Build Your Lineup",
            subtitle = "5 unique categories, 5 players each",
            highlights = listOf(
                OnboardingHighlight(
                    emoji = "\uD83D\uDFE6", // 🟦
                    category = "Serve",
                    description = "Aces, service winners, and clutch holds",
                    color = "#2196F3"
                ),
                OnboardingHighlight(
                    emoji = "\uD83D\uDFE9", // 🟩
                    category = "Forehand",
                    description = "Forehand winners and aggressive play",
                    color = "#4CAF50"
                ),
                OnboardingHighlight(
                    emoji = "\uD83D\uDFE8", // 🟨
                    category = "Backhand",
                    description = "Backhand winners and consistency",
                    color = "#FFC107"
                ),
                OnboardingHighlight(
                    emoji = "\uD83D\uDFE7", // 🟧
                    category = "Return",
                    description = "Break points converted and return winners",
                    color = "#FF9800"
                ),
                OnboardingHighlight(
                    emoji = "\uD83D\uDFEA", // 🟪
                    category = "Net Game",
                    description = "Volleys, drop shots, and net points",
                    color = "#9C27B0"
                )
            )
        ),
        OnboardingScreen(
            emoji = "\uD83D\uDCA1", // 💡
            title = "Strategy Tips",
            subtitle = "How to build a winning lineup",
            highlights = listOf(
                OnboardingHighlight(
                    emoji = "\uD83D\uDCCA", // 📊
                    category = "Skill Ratings",
                    description = "Each player has ratings per category - check them before picking!",
                    color = "#2196F3"
                ),
                OnboardingHighlight(
                    emoji = "\uD83D\uDCB5", // 💵
                    category = "Salary Cap",
                    description = "Stay under \$35K budget - balance stars with value picks",
                    color = "#4CAF50"
                ),
                OnboardingHighlight(
                    emoji = "\uD83C\uDFBE", // 🎾
                    category = "Match Scheduling",
                    description = "Pick players with matches in the contest window",
                    color = "#FF9800"
                )
            )
        )
    )

    val isLastScreen = currentScreen == screens.size - 1

    // Modal overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.7))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "3000")
            property("animation", "fadeIn 0.3s ease-out")
        }
    }) {
        // Modal content
        Div({
            style {
                backgroundColor(colors.surface)
                borderRadius(24.px)
                padding(32.px)
                maxWidth(480.px)
                width(90.percent)
                maxHeight(90.percent)
                overflowY("auto")
                property("box-shadow", "0 16px 48px rgba(0, 0, 0, 0.3)")
                property("animation", "modalSlideIn 0.4s ease-out")
            }
            onClick { it.stopPropagation() }
        }) {
            val screen = screens[currentScreen]

            // Skip button (top right)
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.FlexEnd)
                    marginBottom(8.px)
                }
            }) {
                Span({
                    style {
                        color(colors.textSecondary)
                        fontSize(14.px)
                        cursor("pointer")
                        padding(4.px, 8.px)
                        borderRadius(4.px)
                    }
                    onClick {
                        WebPreferences.setOnboardingCompleted()
                        onComplete()
                    }
                    onMouseOver {
                        (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                            colors.surfaceVariant.toString()
                    }
                    onMouseOut {
                        (it.currentTarget as? org.w3c.dom.HTMLElement)?.style?.backgroundColor =
                            "transparent"
                    }
                }) {
                    Text("Skip")
                }
            }

            // Emoji header
            Div({
                style {
                    fontSize(72.px)
                    textAlign("center")
                    marginBottom(16.px)
                    property("animation", "celebrationBounce 0.6s ease-out")
                }
            }) {
                Text(screen.emoji)
            }

            // Title
            H1({
                style {
                    fontSize(28.px)
                    fontWeight(700)
                    color(colors.textPrimary)
                    textAlign("center")
                    margin(0.px, 0.px, 8.px, 0.px)
                }
            }) {
                Text(screen.title)
            }

            // Subtitle
            P({
                style {
                    fontSize(16.px)
                    color(colors.textSecondary)
                    textAlign("center")
                    margin(0.px, 0.px, 24.px, 0.px)
                }
            }) {
                Text(screen.subtitle)
            }

            // Highlights
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(12.px)
                    marginBottom(32.px)
                }
            }) {
                screen.highlights.forEach { highlight ->
                    HighlightCard(
                        highlight = highlight,
                        colors = colors
                    )
                }
            }

            // Progress dots
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.Center)
                    gap(8.px)
                    marginBottom(24.px)
                }
            }) {
                screens.indices.forEach { index ->
                    Div({
                        style {
                            width(if (index == currentScreen) 24.px else 8.px)
                            height(8.px)
                            borderRadius(4.px)
                            backgroundColor(
                                if (index == currentScreen)
                                    colors.primary
                                else
                                    colors.surfaceVariant
                            )
                            property("transition", "all 0.3s ease")
                            cursor("pointer")
                        }
                        onClick { currentScreen = index }
                    })
                }
            }

            // Action buttons
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(12.px)
                }
            }) {
                // Back button (except on first screen)
                if (currentScreen > 0) {
                    Div({
                        style {
                            flex(1)
                        }
                    }) {
                        Button(
                            text = "Back",
                            onClick = { currentScreen-- },
                            variant = "secondary",
                            fullWidth = true
                        )
                    }
                }

                // Next/Start button
                Div({
                    style {
                        flex(if (currentScreen > 0) 1 else 0)
                        if (currentScreen == 0) {
                            width(100.percent)
                        }
                    }
                }) {
                    Button(
                        text = if (isLastScreen) "Start Playing" else "Next",
                        onClick = {
                            if (isLastScreen) {
                                WebPreferences.setOnboardingCompleted()
                                onComplete()
                            } else {
                                currentScreen++
                            }
                        },
                        variant = "primary",
                        fullWidth = true
                    )
                }
            }
        }
    }
}

@Composable
private fun HighlightCard(
    highlight: OnboardingHighlight,
    colors: com.fantasytenis.web.theme.ThemeColors
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.FlexStart)
            gap(12.px)
            padding(12.px, 16.px)
            backgroundColor(colors.surfaceVariant)
            borderRadius(12.px)
            property("border-left", "4px solid ${highlight.color}")
        }
    }) {
        // Emoji
        Span({
            style {
                fontSize(24.px)
                lineHeight("1")
            }
        }) {
            Text(highlight.emoji)
        }

        // Content
        Div({
            style {
                flex(1)
            }
        }) {
            Div({
                style {
                    fontWeight(600)
                    fontSize(14.px)
                    color(colors.textPrimary)
                    marginBottom(2.px)
                }
            }) {
                Text(highlight.category)
            }
            Div({
                style {
                    fontSize(13.px)
                    color(colors.textSecondary)
                    lineHeight("1.4")
                }
            }) {
                Text(highlight.description)
            }
        }
    }
}
